import PreferredLanguageDropdown from "@/components/Register/preferredLanguageDropdown.component";
import {
  RegistrationUserDetails,
  ValidationErrors,
} from "@/components/Register/registrationForm.component";
import { Checkbox } from "@/shadcn/components/ui/checkbox";
import { Input } from "@/shadcn/components/ui/input";
import { Label } from "@/shadcn/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shadcn/components/ui/select";
import React from "react";

type UserDetailsProps = {
  userDetails: RegistrationUserDetails;
  setUserDetails: React.Dispatch<React.SetStateAction<RegistrationUserDetails>>;
  errors: ValidationErrors;
};

const UserDetails: React.FC<UserDetailsProps> = ({
  userDetails,
  setUserDetails,
  errors,
}) => {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      {/* User Full Name */}
      <div className="space-y-2">
        <Label htmlFor="fullName">
          Full Name <span className="text-red-500">*</span>
        </Label>
        <Input
          id="fullName"
          value={userDetails.fullName}
          onChange={(e) =>
            setUserDetails({ ...userDetails, fullName: e.target.value })
          }
          placeholder="Enter your full name"
        />
        {errors.fullName && (
          <p className="text-sm text-red-500">{errors.fullName}</p>
        )}
      </div>
      {/* User Title */}
      <div className="space-y-2">
        <Label htmlFor="title">
          Title <span className="text-red-500">*</span>
        </Label>
        <Select
          value={userDetails.title}
          onValueChange={(value: RegistrationUserDetails["title"]) =>
            setUserDetails({ ...userDetails, title: value })
          }
        >
          <SelectTrigger className="w-full max-w-full truncate">
            <SelectValue placeholder="Select title" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Mr.">Mr.</SelectItem>
            <SelectItem value="Mrs.">Mrs.</SelectItem>
            <SelectItem value="Ms.">Ms.</SelectItem>
            <SelectItem value="Dr.">Dr.</SelectItem>
            <SelectItem value="Prof.">Prof.</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {/* User Designation */}
      <div className="space-y-2">
        <Label htmlFor="designation">Designation</Label>
        <Input
          id="designation"
          value={userDetails.designation}
          onChange={(e) =>
            setUserDetails({
              ...userDetails,
              designation: e.target.value,
            })
          }
          placeholder="Enter your designation"
        />
      </div>
      {/* User Preferred Language */}
      <PreferredLanguageDropdown
        selectedLanguage={userDetails.preferredLanguage}
        onLanguageChange={(language) =>
          setUserDetails({
            ...userDetails,
            preferredLanguage: language,
          })
        }
        error={errors.preferredLanguage}
      />
      {/* User Email */}
      <div className="space-y-2">
        <Label htmlFor="email">
          Email <span className="text-red-500">*</span>
        </Label>
        <Input
          id="email"
          type="email"
          value={userDetails.email}
          onChange={(e) =>
            setUserDetails({
              ...userDetails,
              email: e.target.value.toLowerCase(),
            })
          }
          placeholder="Enter your email"
        />
        {errors.email && <p className="text-sm text-red-500">{errors.email}</p>}
      </div>
      {/* User Phone */}
      <div className="space-y-2">
        <Label htmlFor="phone">
          Phone <span className="text-red-500">*</span>
        </Label>
        <Input
          id="phone"
          type="tel"
          value={userDetails.phone}
          onChange={(e) =>
            setUserDetails({ ...userDetails, phone: e.target.value })
          }
          placeholder="Enter your phone number"
        />
        {errors.phone && <p className="text-sm text-red-500">{errors.phone}</p>}
      </div>
      {/* User Password */}
      <div className="space-y-2">
        <Label htmlFor="password">
          Password <span className="text-red-500">*</span>
        </Label>
        <Input
          id="password"
          type="password"
          value={userDetails.password}
          onChange={(e) =>
            setUserDetails({
              ...userDetails,
              password: e.target.value.replace(/\s/g, ""),
            })
          }
          placeholder="Enter your password"
        />
        {errors.password && (
          <p className="text-sm text-red-500">{errors.password}</p>
        )}
      </div>
      {/* User Confirm Password */}
      <div className="space-y-2">
        <Label htmlFor="confirmPassword">
          Confirm Password <span className="text-red-500">*</span>
        </Label>
        <Input
          id="confirmPassword"
          type="password"
          value={userDetails.confirmPassword}
          onChange={(e) =>
            setUserDetails({
              ...userDetails,
              confirmPassword: e.target.value.replace(/\s/g, ""),
            })
          }
          placeholder="Confirm your password"
        />
        {errors.confirmPassword && (
          <p className="text-sm text-red-500">{errors.confirmPassword}</p>
        )}
      </div>
      {/* User Preferred Communication Method */}
      <div className="space-y-2">
        <Label htmlFor="preferredCommunicationMethod">
          Preferred Communication Method <span className="text-red-500">*</span>
        </Label>
        {/* Use checkboxes for multiselect because user can select multiple methods */}
        <div className="flex flex-col gap-2">
          <label className="flex items-center gap-2">
            <Checkbox
              id="preferredCommunicationMethodEmail"
              checked={userDetails.preferredCommunicationMethods.includes(
                "email"
              )}
              onCheckedChange={(e) => {
                // Add or remove 'email' from array based on checked state
                setUserDetails({
                  ...userDetails,
                  preferredCommunicationMethods: e
                    ? [...userDetails.preferredCommunicationMethods, "email"]
                    : userDetails.preferredCommunicationMethods.filter(
                        (m) => m !== "email"
                      ),
                });
              }}
            />
            <span className="select-none">Email</span>
          </label>
          <label className="flex items-center gap-2">
            <Checkbox
              id="preferredCommunicationMethodSMS"
              checked={userDetails.preferredCommunicationMethods.includes(
                "sms"
              )}
              onCheckedChange={(e) => {
                setUserDetails({
                  ...userDetails,
                  preferredCommunicationMethods: e
                    ? [...userDetails.preferredCommunicationMethods, "sms"]
                    : userDetails.preferredCommunicationMethods.filter(
                        (m) => m !== "sms"
                      ),
                });
              }}
            />
            <span className="select-none">SMS</span>
          </label>
          {/* <label className="flex items-center gap-2">
            <Checkbox
              id="preferredCommunicationMethodPushNotifications"
              checked={userDetails.preferredCommunicationMethods.includes(
                "push_notification"
              )}
              onCheckedChange={(e) => {
                setUserDetails({
                  ...userDetails,
                  preferredCommunicationMethods: e
                    ? [
                        ...userDetails.preferredCommunicationMethods,
                        "push_notification",
                      ]
                    : userDetails.preferredCommunicationMethods.filter(
                        (m) => m !== "push_notification"
                      ),
                });
              }}
            />
            <span className="select-none">Push Notifications</span>
          </label> */}
        </div>
        {errors.preferredCommunicationMethods && (
          <p className="text-sm text-red-500">
            {errors.preferredCommunicationMethods}
          </p>
        )}
      </div>
    </div>
  );
};

export default UserDetails;
